/**
 * The Slack line under the signed-in email in the TopNav session controls.
 *
 * - Linked (via `/triage link` in Slack): `Slack: @<name>` plus an **Unlink**
 *   button. Unlink asks once inline ("Unlink Slack? Yes · Cancel"), then calls
 *   `POST /api/slack-link/unlink` and refreshes the session query, so the line
 *   flips to "Slack: not linked".
 * - Not linked: `Slack: not linked`, with a tooltip saying how to link. The
 *   words themselves are a button that opens the same steps in a small panel
 *   under them (PKG 10 adoption, Galley #30422): a tooltip never shows on a
 *   touch screen, and a mouse has to know to hover. Closed, the line is
 *   exactly today's text and nothing else, so the session column keeps its
 *   width and the nav beside it never reflows (an extra "How?" widened it
 *   enough at 640 px to push a nav item onto a new row, #31165). Open, the
 *   panel floats like the nav's own menus (out of the flow, Escape or a click
 *   outside closes it), so opening it moves nothing either: steps in the
 *   column's flow widened it to 16rem and stacked the nav at 768 px (#31267).
 *   The panel hangs from the nearest positioned ancestor, TopNav's session
 *   column, so it opens below the whole column and covers nothing in the bar
 *   (#31352); this wrapper is deliberately not `relative`.
 *   The steps are what
 *   `/triage link` really does: the link's lifetime and the confirm button's
 *   label come from `@/lib/slack-link`, which `LinkSlack.tsx` and a Rust test
 *   on `CODE_TTL_MINUTES` share.
 *
 * The name shown is the Slack display name, then the full name, then the id
 * (`slackIdentityLabel`). Mentions use the stored Slack user id, never the name.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { slackIdentityLabel, unlinkSlack, type SlackIdentity } from "@/lib/api";
import { LINK_ACCOUNTS_LABEL, LINK_CODE_MINUTES } from "@/lib/slack-link";

const LINE = "text-[11px] leading-tight";
const MUTED_BUTTON =
  "underline decoration-dotted underline-offset-2 hover:text-gray-800 " +
  "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-500 " +
  "disabled:cursor-not-allowed disabled:opacity-60 dark:hover:text-gray-200";

export function SlackLinkStatus({
  slack,
}: {
  slack: SlackIdentity | null | undefined;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [showHow, setShowHow] = useState(false);
  const howId = useId();
  const howRef = useRef<HTMLSpanElement | null>(null);

  // Escape + click-outside dismissal while the steps are open, as the nav's
  // menus do (TopNav's NavMenu).
  useEffect(() => {
    if (!showHow) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setShowHow(false);
    }
    function onPointerDown(e: MouseEvent) {
      if (howRef.current && e.target instanceof Node && !howRef.current.contains(e.target)) {
        setShowHow(false);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [showHow]);
  const unlink = useMutation({
    mutationFn: unlinkSlack,
    retry: 0,
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
    },
  });

  if (!slack) {
    return (
      <span
        ref={howRef}
        className={`${LINE} flex flex-col items-end text-gray-400 dark:text-gray-500`}
      >
        <button
          type="button"
          className={MUTED_BUTTON}
          title="Type /triage link in Slack to connect your Slack account"
          aria-expanded={showHow}
          aria-controls={howId}
          aria-label="Slack: not linked. How to link your Slack account"
          onClick={() => setShowHow((open) => !open)}
        >
          Slack: not linked
        </button>
        {showHow && (
          <span
            id={howId}
            data-testid="slack-link-how"
            className="absolute right-0 top-full z-50 mt-1 w-64 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-right text-gray-600 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
          >
            In Slack, type <code>/triage link</code>. It sends you a private link that works for{" "}
            {LINK_CODE_MINUTES} minutes: open it here and press <strong>{LINK_ACCOUNTS_LABEL}</strong>.
          </span>
        )}
      </span>
    );
  }

  const name = slackIdentityLabel(slack);
  return (
    <span
      className={`${LINE} flex items-center gap-1.5 text-gray-500 dark:text-gray-400`}
    >
      <span
        className="max-w-[10rem] truncate"
        title={`Slack ${slack.real_name ?? name} (${slack.user_id})`}
      >
        Slack: @{name}
      </span>
      {confirming ? (
        <span role="group" aria-label="Confirm unlinking Slack" className="flex gap-1.5">
          <span>Unlink?</span>
          <button
            type="button"
            className={`${MUTED_BUTTON} text-red-700 dark:text-red-400`}
            disabled={unlink.isPending}
            onClick={() => unlink.mutate()}
          >
            Yes
          </button>
          <button
            type="button"
            className={MUTED_BUTTON}
            disabled={unlink.isPending}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </button>
        </span>
      ) : (
        <button
          type="button"
          className={MUTED_BUTTON}
          aria-label={`Unlink Slack account @${name}`}
          onClick={() => setConfirming(true)}
        >
          Unlink
        </button>
      )}
      {unlink.isError && (
        <span role="alert" className="text-red-700 dark:text-red-400" title={unlink.error.message}>
          Unlink failed
        </span>
      )}
    </span>
  );
}
